/**
 * Scrolling over a number field must never change it.
 *
 * Chrome, Edge and Safari step a FOCUSED `<input type="number">` up or down
 * when a wheel or trackpad scroll lands on it, even with the spinner hidden
 * (globals.css strips it). An organiser types a fee, scrolls to reach Save
 * with the pointer still over the box, and saves a different number without
 * noticing.
 *
 * The browser only steps the field from its default wheel handling, and only
 * while the field has focus. So one document-level listener, in the capture
 * phase (it runs before any handler on the page can stop the event), blurs a
 * focused number field the moment a wheel event reaches it. By the time the
 * browser's default handling runs the field is no longer focused, so it does
 * not step, and the wheel goes on to scroll the page as normal.
 *
 * Blur rather than preventDefault on purpose: preventDefault would also stop
 * the page scrolling, and would need a non-passive listener, which makes every
 * scroll in the app wait on the main thread. This listener is passive.
 *
 * Arrow keys still step the field (that is deliberate input), and touch
 * scrolling never fires wheel events, so neither is affected.
 */

function isFocusedNumberInput(node: EventTarget | null | undefined): node is HTMLInputElement {
  if (!node || (node as Node).nodeType !== 1) return false;
  const el = node as HTMLInputElement;
  // tagName and type, not instanceof: a field in an iframe belongs to another
  // window's HTMLInputElement.
  if (el.tagName !== 'INPUT' || el.type !== 'number') return false;
  const root = el.getRootNode() as Document | ShadowRoot;
  return root.activeElement === el;
}

export function blurFocusedNumberInputOnWheel(event: Event): void {
  // composedPath()[0] is the real target even inside a shadow root, where
  // event.target is retargeted to the host.
  const target = typeof event.composedPath === 'function' ? event.composedPath()[0] : event.target;
  if (isFocusedNumberInput(target)) target.blur();
}

/** Installs the guard on a document; returns the function that removes it. */
export function installNumberInputWheelGuard(doc: Document): () => void {
  const options: AddEventListenerOptions = { capture: true, passive: true };
  doc.addEventListener('wheel', blurFocusedNumberInputOnWheel, options);
  return () => doc.removeEventListener('wheel', blurFocusedNumberInputOnWheel, options);
}
